/**
 * Cliente API centralizado con manejo de autenticación
 * Detecta tokens expirados y redirige al login automáticamente
 */
import { appConfig } from "@/config/app-config";

// Evento global para notificar cuando el token expira
export const AUTH_EXPIRED_EVENT = "auth:expired";

/**
 * Dispara un evento global cuando el token expira
 */
export function notifyAuthExpired() {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/**
 * Obtiene el token de autenticación del sessionStorage
 */
export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    // Intentar con la clave principal
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (oidcData) {
      const parsed = JSON.parse(oidcData);
      if (parsed.access_token) {
        return parsed.access_token;
      }
    }
    // Si no encontramos, buscar cualquier clave que empiece con oidc.user:
    const allKeys = Object.keys(sessionStorage);
    for (const key of allKeys) {
      if (key.startsWith("oidc.user:")) {
        const data = sessionStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed.access_token) {
              return parsed.access_token;
            }
          } catch {
            // Continuar con la siguiente clave
          }
        }
      }
    }
  } catch {
    // Error obteniendo el token
  }
  return null;
}

/**
 * Wrapper de fetch que maneja automáticamente el 401
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  
  const isMultipart = options.body instanceof FormData;
  const defaultHeaders: HeadersInit = {
    Authorization: token ? `Bearer ${token}` : "",
  };
  if (!isMultipart) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  });

  // ✅ Detectar token expirado (401 Unauthorized)
  if (response.status === 401) {
    console.warn("[API Client] Token expirado o inválido (401). Notificando...");
    notifyAuthExpired();
  }

  return response;
}

/**
 * Verificar si el token actual está expirado
 */
export function isTokenExpired(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (!oidcData) return true;
    
    const parsed = JSON.parse(oidcData);
    if (!parsed.expires_at) return false;
    
    // Verificar si el token expira en los próximos 60 segundos
    const expiresAt = parsed.expires_at * 1000; // convertir a ms
    return Date.now() >= expiresAt - 60000;
  } catch {
    return true;
  }
}
